import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { clsx } from 'clsx';
import toast from 'react-hot-toast';
import MarkdownMessage from '../../components/chat/MarkdownMessage';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { wikiApi, type WikiPageView } from '../../services/wiki-api';
import type { ChatMessage, V2StreamEvent } from '@/types/chat';
import { splitBlocks, changedBlockIndices } from './markdownBlocks';

interface DiscussMessage {
  role: 'user' | 'assistant';
  text: string;
  /** Versions saved by edits made during this assistant turn. */
  savedVersions: number[];
}

const SUGGESTIONS = [
  "What's missing from this page?",
  'Summarize recent changes',
  'Anything on this page that looks outdated?',
];

const INTERVIEW_OPENER =
  "Interview me and draft this business profile — ask me about the business and fill the page in as we go.";

/**
 * ✦ Discuss with AI (D27/D47): conversation left, live document right.
 * Streams through the wiki discuss endpoint; every applied edit arrives as
 * a revision_saved event, refreshes the doc pane, and flashes the changed
 * blocks. One persistent thread per page+user (D49).
 */
const DiscussView: React.FC<{ page: WikiPageView; interview: boolean }> = ({ page, interview }) => {
  const queryClient = useQueryClient();
  const [messages, setMessages] = useState<DiscussMessage[]>([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [working, setWorking] = useState<string | null>(null);
  const [savingVersion, setSavingVersion] = useState<number | null>(null);
  const [flashBlocks, setFlashBlocks] = useState<Set<number>>(new Set());
  const historyLoadedFor = useRef<string | null>(null);
  const autoStarted = useRef(false);
  const msgsRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef(page.content);

  const { data: discussState, isLoading } = useQuery({
    queryKey: ['wikiDiscuss', page.id],
    queryFn: () => wikiApi.getDiscuss(page.id),
  });

  // Hydrate local messages from the persistent thread once per page.
  useEffect(() => {
    if (!discussState || historyLoadedFor.current === page.id) return;
    historyLoadedFor.current = page.id;
    setMessages(discussState.messages.map(toDiscussMessage).filter((m) => m.text || m.savedVersions.length));
  }, [discussState, page.id]);

  // The live pane mirrors the page query; flash blocks that changed.
  useEffect(() => {
    if (page.content !== contentRef.current) {
      setFlashBlocks(changedBlockIndices(contentRef.current, page.content));
      contentRef.current = page.content;
      const t = setTimeout(() => setFlashBlocks(new Set()), 4000);
      return () => clearTimeout(t);
    }
  }, [page.content]);

  useEffect(() => {
    msgsRef.current?.scrollTo({ top: msgsRef.current.scrollHeight });
  }, [messages, working]);

  const send = async (text: string): Promise<void> => {
    const message = text.trim();
    if (!message || streaming) return;
    setInput('');
    setStreaming(true);
    setMessages((prev) => [...prev, { role: 'user', text: message, savedVersions: [] }]);

    let assistantStarted = false;
    const appendDelta = (delta: string): void => {
      setMessages((prev) => {
        const next = [...prev];
        if (!assistantStarted || next[next.length - 1]?.role !== 'assistant') {
          assistantStarted = true;
          next.push({ role: 'assistant', text: delta, savedVersions: [] });
        } else {
          next[next.length - 1] = {
            ...next[next.length - 1],
            text: next[next.length - 1].text + delta,
          };
        }
        return next;
      });
    };

    const onEvent = (event: V2StreamEvent): void => {
      switch (event.type) {
        case 'text_delta':
          setWorking(null);
          appendDelta(event.delta as string);
          break;
        case 'tool_start':
          setWorking(
            event.toolName === 'edit_wiki_page'
              ? 'Editing the document…'
              : event.toolName === 'read_wiki_page'
                ? 'Reading the page…'
                : 'Working…',
          );
          break;
        case 'tool_result':
          setWorking(null);
          break;
        case 'revision_saved': {
          const version = event.version as number;
          setSavingVersion(version);
          setMessages((prev) => {
            const next = [...prev];
            const last = next[next.length - 1];
            if (last?.role === 'assistant') {
              next[next.length - 1] = { ...last, savedVersions: [...last.savedVersions, version] };
            } else {
              assistantStarted = true;
              next.push({ role: 'assistant', text: '', savedVersions: [version] });
            }
            return next;
          });
          void queryClient
            .invalidateQueries({ queryKey: ['wikiPage', page.id] })
            .then(() => setSavingVersion(null));
          queryClient.invalidateQueries({ queryKey: ['wikiRevisions', page.id] });
          queryClient.invalidateQueries({ queryKey: ['wikiTree'] });
          break;
        }
        case 'error':
          toast.error((event.error as string) || 'Something went wrong');
          break;
        default:
          break;
      }
    };

    try {
      await wikiApi.discuss(page.id, { message, mode: interview ? 'interview' : 'discuss' }, onEvent);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to reach the AI');
    } finally {
      setStreaming(false);
      setWorking(null);
      setSavingVersion(null);
      queryClient.invalidateQueries({ queryKey: ['wikiDiscuss', page.id] });
    }
  };

  // The Businesses "Interview me and draft it" CTA: auto-open the interview
  // on a fresh thread.
  useEffect(() => {
    if (interview && discussState && !discussState.threadId && !autoStarted.current) {
      autoStarted.current = true;
      void send(INTERVIEW_OPENER);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interview, discussState]);

  const blocks = useMemo(() => splitBlocks(page.content), [page.content]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)]">
      {/* Chat column */}
      <div className="flex flex-col min-h-[520px] max-h-[70vh] border-b xl:border-b-0 xl:border-r border-border">
        <div ref={msgsRef} className="flex-1 overflow-y-auto p-4 flex flex-col gap-2.5">
          {messages.length === 0 && !streaming && (
            <p className="text-[12.5px] text-muted-foreground text-center my-auto px-6">
              Tell the AI what to fix, add, or verify on this page — every change it makes is saved
              as a revision you can review or revert.
            </p>
          )}
          {messages.map((m, i) => (
            <div
              key={i}
              className={clsx(
                'max-w-[90%] text-[12.5px] leading-relaxed rounded-xl px-3 py-2',
                m.role === 'user'
                  ? 'self-end bg-primary text-primary-foreground rounded-br-[4px]'
                  : 'self-start bg-muted text-foreground rounded-bl-[4px]',
              )}
            >
              {m.role === 'assistant' ? <MarkdownMessage content={m.text} /> : m.text}
              {m.savedVersions.length > 0 && (
                <div className="mt-1.5 text-[10.5px] font-bold text-success">
                  ✓ Saved as {m.savedVersions.map((v) => `v${v}`).join(', ')}
                </div>
              )}
            </div>
          ))}
          {working && (
            <div className="self-start flex items-center gap-2 text-[12px] font-semibold text-primary px-1 py-1">
              <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
              {working}
            </div>
          )}
          {streaming && !working && messages[messages.length - 1]?.role === 'user' && (
            <div className="self-start flex items-center gap-2 text-[12px] font-semibold text-primary px-1 py-1">
              <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
              Thinking…
            </div>
          )}
        </div>

        {messages.length === 0 && !streaming && !interview && (
          <div className="flex flex-wrap gap-1.5 px-4 pb-2.5">
            {SUGGESTIONS.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => void send(s)}
                className="text-[11px] rounded-[9px] bg-muted border border-border px-2.5 py-1 text-foreground hover:bg-accent"
              >
                {s}
              </button>
            ))}
          </div>
        )}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            void send(input);
          }}
          className="flex gap-2 px-3.5 py-3 border-t border-border"
        >
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Tell the AI what to fix, add, or verify…"
            className="flex-1 rounded-[10px] border border-input bg-background px-3 py-2 text-[12.5px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
          />
          <button
            type="submit"
            disabled={streaming || !input.trim()}
            className="rounded-[10px] bg-primary text-primary-foreground px-3.5 py-2 text-[12.5px] font-semibold disabled:opacity-50"
          >
            Send
          </button>
        </form>
      </div>

      {/* Live document */}
      <div>
        <div className="flex items-center gap-2 px-4.5 py-2.5 border-b border-border text-[11.5px] text-muted-foreground px-4">
          <span className="inline-flex items-center gap-1.5 text-[9.5px] font-extrabold tracking-wider text-success bg-success/10 rounded-full px-2 py-0.5">
            <span className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" /> LIVE
          </span>
          The document updates as the AI edits — every change becomes a normal revision
          {savingVersion !== null && (
            <span className="ml-auto text-[10.5px] font-semibold text-primary">
              saving v{savingVersion}…
            </span>
          )}
        </div>
        <div className="px-5 py-4 max-h-[64vh] overflow-y-auto">
          {blocks.map((block, i) => (
            <div
              key={`${page.version}-${i}`}
              className={clsx(
                flashBlocks.has(i) &&
                  'border-l-[3px] border-success bg-success/10 rounded-r-lg pl-3 pr-2 py-1 my-1 animate-pulse [animation-iteration-count:2]',
              )}
            >
              {flashBlocks.has(i) && (
                <span className="block text-[9px] font-extrabold tracking-wider text-success mb-0.5">
                  ✦ JUST UPDATED
                </span>
              )}
              <MarkdownMessage content={block} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

function toDiscussMessage(m: ChatMessage): DiscussMessage {
  const text = m.segments
    .filter((s): s is Extract<typeof s, { type: 'text' }> => s.type === 'text')
    .map((s) => s.text)
    .join('\n\n');
  return {
    role: m.role === 'user' ? 'user' : 'assistant',
    text,
    savedVersions: [],
  };
}

export default DiscussView;
